// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import {
  CircleDashedIcon,
  CircleEllipsisIcon,
  CircleIcon,
  CircleOffIcon,
  RocketIcon,
  SettingsIcon,
  UserIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { UserPlan } from '@/models/user';
import type { User } from '@/models';
import type { UserStatus } from '@/context/status-context/status-context';
import { useStatus } from '@/context/status-context/use-status';
import { cn } from '@/lib/utils';
import { avatarPlaceholder } from '@/lib/avatar-placeholder';
import { useSocket } from '@/context/socket-context/use-external-socket';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/auth-context/use-auth';
import { API_BASE } from '@/config';
import { useNavigate } from 'react-router';
import { planGlowClasses } from '@/components/server/server-member.tsx';
import { SilentSignIn } from '@/components/auth/silent-sign-in.tsx';
import { IoNotifications } from 'react-icons/io5';
import { MdRecordVoiceOver } from 'react-icons/md';

export const UserButton = () => {
  const { user: sessionUser } = useAuth();
  const userCyberKoalaId = Cookies.get('cyberkoalaIdUser');
  const userJson = userCyberKoalaId ? JSON.parse(userCyberKoalaId) : {};
  const socket = useSocket();
  const { t } = useTranslation();
  const [plan, setPlan] = useState(UserPlan.FREE);
  const [user, setUser] = useState<User | null>(null);
  const { status, setStatus, description, updatedAt, setUpdatedAt } =
    useStatus();
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionUser) {
      return;
    }
    fetch(`${API_BASE}/api/users/${sessionUser.id}`)
      .then((res) => res.json())
      .then((data) => {
        setPlan(data.plan);
        setUser(data);
      })
      .catch(() => {});
   }, [sessionUser]);

  const handleSaveStatus = async (status: UserStatus) => {
    const now = new Date();
    setStatus(status);
    setUpdatedAt(now);

    if (!socket || !sessionUser?.id) return;
    socket.send(
      JSON.stringify({
        type: 'update-presence',
        userId: sessionUser.id,
        status: status,
        description,
        updatedAt: now.toISOString(),
      })
    );
  };

  const isActive = status !== 'offline' && status !== 'invisible';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Avatar
          className={cn(
            'rounded-full border-2 transition-all duration-300',
            status === 'online' ? 'border-green-500' : '',
            status === 'away' ? 'border-yellow-500' : '',
            status === 'dnd' ? 'border-red-500' : '',
            status === 'invisible' ? 'border-zinc-500' : '',
            ['😀', '☹️', '🎮', '💡'].includes(status) && 'border-pink-500',
            isActive && planGlowClasses[user?.plan ?? UserPlan.BASIC]
          )}
        >
          <AvatarImage
            src={
              user?.image ||
              userJson?.picture ||
              avatarPlaceholder({ name: user?.name || user?.email })
            }
            className="transition-all duration-300"
          />
          <AvatarFallback className="bg-sky-500">
            <UserIcon className="text-white" />
          </AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="ml-2 mb-2 w-72 shadow-sm backdrop-blur-md bg-white dark:bg-[#292b30]"
        align="end"
      >
        <DropdownMenuItem onClick={() => navigate(`/channels/@me/boost`)}>
          <RocketIcon className="mr-2 size-4" />
          {plan}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/channels/@me/account`)}>
          <SettingsIcon className="mr-2 size-4" /> {t('MeSettingsSidebar.my_account')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/channels/@me/voice-video`)}>
          <MdRecordVoiceOver className="mr-2 size-4" /> {t('MeSettingsSidebar.voice_n_video')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate(`/channels/@me/notifications`)}>
          <IoNotifications className="mr-2 size-4" /> {t('MeSettingsSidebar.notifications')}
        </DropdownMenuItem>
        <hr />
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => handleSaveStatus('online')}>
            <CircleIcon className="mr-2 size-4 text-green-500" />
            {t('UserButton.onlineStatus')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSaveStatus('away')}>
            <CircleDashedIcon className="mr-2 size-4 text-yellow-500" />
            {t('UserButton.awayStatus')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSaveStatus('dnd')}>
            <CircleOffIcon className="mr-2 size-4 text-red-500" />
            {t('UserButton.dndStatus')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleSaveStatus('invisible')}>
            <CircleEllipsisIcon className="mr-2 size-4" />
            {t('UserButton.invisibleStatus')}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuItem className="flex items-center gap-x-2">
          <button onClick={() => handleSaveStatus('😀')}>😀</button>
          <button onClick={() => handleSaveStatus('☹️')}>☹️</button>
          <button onClick={() => handleSaveStatus('🎮')}>🎮</button>
          <button onClick={() => handleSaveStatus('💡')}>💡</button>
        </DropdownMenuItem>
        <SilentSignIn />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
